import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Palette, Layout, Image as ImageIcon, Music, Eye, Save, 
  RotateCcw, X, Upload, Monitor, Smartphone, Check,
  AlertTriangle, Sparkles
} from 'lucide-react';

const API = '/api';

// Color picker component with hex input
const ColorPicker = ({ label, value, onChange, description }) => {
  const [localValue, setLocalValue] = useState(value || '#000000');
  
  useEffect(() => {
    setLocalValue(value || '#000000');
  }, [value]);
  
  const handleColorChange = (newValue) => {
    setLocalValue(newValue);
    if (/^#[0-9A-Fa-f]{6}$/.test(newValue)) {
      onChange(newValue);
    }
  };
  
  return (
    <div className="space-y-2">
      <Label className="text-gray-300 text-sm">{label}</Label>
      {description && <p className="text-xs text-gray-500">{description}</p>}
      <div className="flex items-center gap-3">
        <input
          type="color"
          value={localValue}
          onChange={(e) => handleColorChange(e.target.value)}
          className="w-12 h-10 rounded-lg border-2 border-gray-600 cursor-pointer bg-transparent"
        />
        <Input
          value={localValue}
          onChange={(e) => handleColorChange(e.target.value)}
          placeholder="#000000"
          className="flex-1 bg-gray-800 border-gray-600 text-white font-mono text-sm"
          maxLength={7}
        />
      </div>
    </div>
  );
};

// Image uploader component
const ImageUploader = ({ label, value, onUpload, endpoint, description, aspectRatio = "3:1" }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value);
  
  useEffect(() => {
    setPreview(value);
  }, [value]);
  
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, WebP, or GIF image');
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}${endpoint}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const url = response.data.url;
      setPreview(url);
      onUpload(url);
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };
  
  const handleRemove = () => {
    setPreview(null);
    onUpload(null);
  };
  
  return (
    <div className="space-y-2">
      <Label className="text-gray-300 text-sm">{label}</Label>
      {description && <p className="text-xs text-gray-500">{description}</p>}
      
      <div className="relative">
        {preview ? (
          <div className="relative group">
            <img 
              src={preview} 
              alt={label}
              className="w-full h-32 object-cover rounded-lg border border-gray-600"
            />
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 rounded-lg">
              <label className="cursor-pointer">
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleFileChange}
                  className="sr-only"
                  disabled={uploading}
                />
                <Button size="sm" variant="secondary" asChild>
                  <span><Upload className="w-4 h-4 mr-1" /> Replace</span>
                </Button>
              </label>
              <Button size="sm" variant="destructive" onClick={handleRemove}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ) : (
          <label className="cursor-pointer block">
            <input 
              type="file" 
              accept="image/*" 
              onChange={handleFileChange}
              className="sr-only"
              disabled={uploading}
            />
            <div className={`
              border-2 border-dashed border-gray-600 rounded-lg p-6
              hover:border-orange-500 transition-colors text-center
              ${uploading ? 'opacity-50' : ''}
            `}>
              {uploading ? (
                <div className="flex flex-col items-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mb-2" />
                  <span className="text-gray-400 text-sm">Uploading...</span>
                </div>
              ) : (
                <>
                  <ImageIcon className="w-8 h-8 mx-auto text-gray-500 mb-2" />
                  <span className="text-gray-400 text-sm">Click to upload ({aspectRatio})</span>
                </>
              )}
            </div>
          </label>
        )}
      </div>
    </div>
  );
};

// Main component
const ProfileThemeCustomizer = ({ 
  isOpen, 
  onClose, 
  profileData,
  onThemeUpdate 
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('colors');
  const [previewMode, setPreviewMode] = useState('desktop');
  const [hasChanges, setHasChanges] = useState(false);
  
  // Theme state
  const [theme, setTheme] = useState(null);
  const [originalTheme, setOriginalTheme] = useState(null);
  const [availableFonts, setAvailableFonts] = useState([]);
  const [availableLayouts, setAvailableLayouts] = useState([]);
  
  // Fetch current theme on mount
  useEffect(() => {
    if (isOpen) {
      fetchTheme();
    }
  }, [isOpen]);
  
  const fetchTheme = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/creator/me/theme`);
      const data = response.data;
      
      setTheme(data.theme);
      setOriginalTheme(JSON.parse(JSON.stringify(data.theme)));
      setAvailableFonts(data.available_fonts || []);
      setAvailableLayouts(data.available_layouts || []);
      setHasChanges(false);
    } catch (error) {
      console.error('Error fetching theme:', error);
      toast.error('Failed to load theme settings');
    } finally {
      setLoading(false);
    }
  };
  
  // Update theme locally
  const updateTheme = useCallback((path, value) => {
    setTheme(prev => {
      const newTheme = JSON.parse(JSON.stringify(prev));
      
      // Navigate path and set value
      const keys = path.split('.');
      let current = newTheme;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      
      return newTheme;
    });
    setHasChanges(true);
  }, []);
  
  // Save theme to backend
  const saveTheme = async () => {
    setSaving(true);
    try {
      const payload = {
        layout: theme.layout,
        colors: theme.colors,
        font: theme.font,
        banner: theme.banner,
        background: theme.background,
        sections: theme.sections,
        music_url: theme.music_url
      };
      
      const response = await axios.put(`${API}/creator/me/theme`, payload);
      
      if (response.data.success) {
        setOriginalTheme(JSON.parse(JSON.stringify(response.data.theme)));
        setTheme(response.data.theme);
        setHasChanges(false);
        toast.success('Theme saved successfully!');
        
        if (onThemeUpdate) {
          onThemeUpdate(response.data.theme);
        }
      }
    } catch (error) {
      console.error('Error saving theme:', error);
      toast.error(error.response?.data?.detail || 'Failed to save theme');
    } finally {
      setSaving(false);
    }
  };
  
  // Reset to default
  const resetTheme = async () => {
    if (!window.confirm('Reset your profile theme to default? This cannot be undone.')) {
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.delete(`${API}/creator/me/theme/reset`);
      
      if (response.data.success) {
        setTheme(response.data.theme);
        setOriginalTheme(JSON.parse(JSON.stringify(response.data.theme)));
        setHasChanges(false);
        toast.success('Theme reset to default');
        
        if (onThemeUpdate) {
          onThemeUpdate(response.data.theme);
        }
      }
    } catch (error) {
      console.error('Error resetting theme:', error);
      toast.error('Failed to reset theme');
    } finally {
      setSaving(false);
    }
  };
  
  // Discard changes
  const discardChanges = () => {
    if (hasChanges) {
      if (!window.confirm('Discard all unsaved changes?')) return;
    }
    setTheme(JSON.parse(JSON.stringify(originalTheme)));
    setHasChanges(false);
  };
  
  // Handle close
  const handleClose = () => {
    if (hasChanges) {
      if (!window.confirm('You have unsaved changes. Close anyway?')) return;
    }
    onClose();
  };
  
  if (!isOpen) return null;
  
  // Generate CSS variables for preview
  const getPreviewStyles = () => {
    if (!theme) return {};
    
    const colors = theme.colors || {};
    const bg = theme.background || {};
    
    let backgroundStyle = colors.background || '#1a1a2e';
    if (bg.type === 'gradient' && bg.gradient_start && bg.gradient_end) {
      backgroundStyle = `linear-gradient(${bg.gradient_direction || 'to bottom'}, ${bg.gradient_start}, ${bg.gradient_end})`;
    } else if (bg.type === 'image' && bg.image_url) {
      backgroundStyle = `url(${bg.image_url})`;
    }
    
    return {
      '--theme-bg': colors.background || '#1a1a2e',
      '--theme-card': colors.card || '#16213e',
      '--theme-text': colors.text || '#eaeaea',
      '--theme-heading': colors.heading || '#ffffff',
      '--theme-accent': colors.accent || '#f97316',
      '--theme-link': colors.link || '#60a5fa',
      '--theme-font': theme.font || 'Inter',
      background: bg.type === 'solid' ? colors.background : backgroundStyle,
    };
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl h-[90vh] p-0 bg-gray-900 border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 bg-gray-900">
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-orange-500" />
            <div>
              <DialogTitle className="text-xl font-bold text-white">
                Customize Your Profile
              </DialogTitle>
              <DialogDescription className="text-gray-400 text-sm">
                Make your profile uniquely yours - MySpace style!
              </DialogDescription>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {hasChanges && (
              <span className="text-yellow-400 text-sm mr-2">● Unsaved changes</span>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={discardChanges}
              disabled={!hasChanges || saving}
              className="text-gray-400 hover:text-white"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Discard
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={resetTheme}
              disabled={saving}
              className="text-gray-400 hover:text-white"
            >
              Reset to Default
            </Button>
            <Button 
              onClick={saveTheme}
              disabled={!hasChanges || saving}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Theme
                </>
              )}
            </Button>
          </div>
        </div>
        
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4" />
              <p className="text-gray-400">Loading theme settings...</p>
            </div>
          </div>
        ) : theme ? (
          <div className="flex h-[calc(90vh-80px)]">
            {/* Left Panel - Editor */}
            <div className="w-96 border-r border-gray-700 flex flex-col bg-gray-900">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
                <TabsList className="w-full bg-gray-800 border-b border-gray-700 rounded-none h-12 px-2">
                  <TabsTrigger value="colors" className="flex-1 text-xs">
                    <Palette className="w-4 h-4 mr-1" />
                    Colors
                  </TabsTrigger>
                  <TabsTrigger value="layout" className="flex-1 text-xs">
                    <Layout className="w-4 h-4 mr-1" />
                    Layout
                  </TabsTrigger>
                  <TabsTrigger value="images" className="flex-1 text-xs">
                    <ImageIcon className="w-4 h-4 mr-1" />
                    Images
                  </TabsTrigger>
                  <TabsTrigger value="extras" className="flex-1 text-xs">
                    <Music className="w-4 h-4 mr-1" />
                    Extras
                  </TabsTrigger>
                </TabsList>
                
                <div className="flex-1 overflow-y-auto p-4 space-y-6">
                  {/* Colors Tab */}
                  <TabsContent value="colors" className="mt-0 space-y-6">
                    <Card className="bg-gray-800 border-gray-700">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-white">Page Colors</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <ColorPicker
                          label="Background"
                          value={theme.colors?.background}
                          onChange={(v) => updateTheme('colors.background', v)}
                          description="Main page background"
                        />
                        <ColorPicker
                          label="Card Background"
                          value={theme.colors?.card}
                          onChange={(v) => updateTheme('colors.card', v)}
                          description="Cards and sections"
                        />
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-gray-800 border-gray-700">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-white">Text Colors</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <ColorPicker
                          label="Heading"
                          value={theme.colors?.heading}
                          onChange={(v) => updateTheme('colors.heading', v)}
                        />
                        <ColorPicker
                          label="Body Text"
                          value={theme.colors?.text}
                          onChange={(v) => updateTheme('colors.text', v)}
                        />
                        <ColorPicker
                          label="Links"
                          value={theme.colors?.link}
                          onChange={(v) => updateTheme('colors.link', v)}
                        />
                        <ColorPicker
                          label="Accent"
                          value={theme.colors?.accent}
                          onChange={(v) => updateTheme('colors.accent', v)}
                          description="Buttons, highlights"
                        />
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-gray-800 border-gray-700">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-white">Font</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Select
                          value={theme.font || 'Inter'}
                          onValueChange={(v) => updateTheme('font', v)}
                        >
                          <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-700">
                            {availableFonts.map(font => (
                              <SelectItem 
                                key={font} 
                                value={font}
                                className="text-white hover:bg-gray-700"
                                style={{ fontFamily: font }}
                              >
                                {font}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </CardContent>
                    </Card>
                  </TabsContent>
                  
                  {/* Layout Tab */}
                  <TabsContent value="layout" className="mt-0 space-y-6">
                    <Card className="bg-gray-800 border-gray-700">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-white">Layout Style</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-3">
                          {availableLayouts.map(layout => (
                            <button
                              key={layout}
                              onClick={() => updateTheme('layout', layout)}
                              className={`
                                p-4 rounded-lg border-2 transition-all text-left capitalize
                                ${theme.layout === layout 
                                  ? 'border-orange-500 bg-orange-500/10' 
                                  : 'border-gray-600 hover:border-gray-500'
                                }
                              `}
                            >
                              <div className="text-white font-medium">{layout}</div>
                              {theme.layout === layout && (
                                <Check className="w-4 h-4 text-orange-500 mt-1" />
                              )}
                            </button>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-gray-800 border-gray-700">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-white">Visible Sections</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {[
                          { key: 'show_bio', label: 'Bio / About' },
                          { key: 'show_series', label: 'Comics & Novels' },
                          { key: 'show_gallery', label: 'Art Gallery' },
                          { key: 'show_stats', label: 'Stats' },
                          { key: 'show_fan_wall', label: 'Fan Wall' },
                          { key: 'show_social_links', label: 'Social Links' },
                          { key: 'show_music_player', label: 'Music Player' },
                        ].map(section => (
                          <div key={section.key} className="flex items-center justify-between">
                            <Label className="text-gray-300">{section.label}</Label>
                            <Switch
                              checked={theme.sections?.[section.key] ?? true}
                              onCheckedChange={(v) => updateTheme(`sections.${section.key}`, v)}
                            />
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  </TabsContent>
                  
                  {/* Images Tab */}
                  <TabsContent value="images" className="mt-0 space-y-6">
                    <Card className="bg-gray-800 border-gray-700">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-white">Profile Banner</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <ImageUploader
                          label="Banner Image"
                          value={theme.banner?.url}
                          onUpload={(url) => updateTheme('banner.url', url)}
                          endpoint="/upload/profile-banner"
                          description="Recommended: 1500x500px (3:1 ratio)"
                          aspectRatio="3:1"
                        />
                        
                        {theme.banner?.url && (
                          <>
                            <div>
                              <Label className="text-gray-300 text-sm">Banner Position</Label>
                              <Select
                                value={theme.banner?.position || 'center'}
                                onValueChange={(v) => updateTheme('banner.position', v)}
                              >
                                <SelectTrigger className="bg-gray-700 border-gray-600 text-white mt-2">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-gray-800 border-gray-700">
                                  <SelectItem value="top" className="text-white">Top</SelectItem>
                                  <SelectItem value="center" className="text-white">Center</SelectItem>
                                  <SelectItem value="bottom" className="text-white">Bottom</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div>
                              <Label className="text-gray-300 text-sm">
                                Overlay Darkness: {Math.round((theme.banner?.overlay_opacity || 0.3) * 100)}%
                              </Label>
                              <input
                                type="range"
                                min="0"
                                max="100"
                                value={(theme.banner?.overlay_opacity || 0.3) * 100}
                                onChange={(e) => updateTheme('banner.overlay_opacity', e.target.value / 100)}
                                className="w-full mt-2"
                              />
                            </div>
                          </>
                        )}
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-gray-800 border-gray-700">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-white">Page Background</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <Label className="text-gray-300 text-sm">Background Type</Label>
                          <Select
                            value={theme.background?.type || 'solid'}
                            onValueChange={(v) => updateTheme('background.type', v)}
                          >
                            <SelectTrigger className="bg-gray-700 border-gray-600 text-white mt-2">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-gray-800 border-gray-700">
                              <SelectItem value="solid" className="text-white">Solid Color</SelectItem>
                              <SelectItem value="gradient" className="text-white">Gradient</SelectItem>
                              <SelectItem value="image" className="text-white">Image</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {theme.background?.type === 'gradient' && (
                          <>
                            <ColorPicker
                              label="Gradient Start"
                              value={theme.background?.gradient_start || theme.colors?.background}
                              onChange={(v) => updateTheme('background.gradient_start', v)}
                            />
                            <ColorPicker
                              label="Gradient End"
                              value={theme.background?.gradient_end || '#000000'}
                              onChange={(v) => updateTheme('background.gradient_end', v)}
                            />
                            <div>
                              <Label className="text-gray-300 text-sm">Direction</Label>
                              <Select
                                value={theme.background?.gradient_direction || 'to bottom'}
                                onValueChange={(v) => updateTheme('background.gradient_direction', v)}
                              >
                                <SelectTrigger className="bg-gray-700 border-gray-600 text-white mt-2">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-gray-800 border-gray-700">
                                  <SelectItem value="to bottom" className="text-white">↓ Top to Bottom</SelectItem>
                                  <SelectItem value="to top" className="text-white">↑ Bottom to Top</SelectItem>
                                  <SelectItem value="to right" className="text-white">→ Left to Right</SelectItem>
                                  <SelectItem value="to left" className="text-white">← Right to Left</SelectItem>
                                  <SelectItem value="to bottom right" className="text-white">↘ Diagonal</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </>
                        )}
                        
                        {theme.background?.type === 'image' && (
                          <>
                            <ImageUploader
                              label="Background Image"
                              value={theme.background?.image_url}
                              onUpload={(url) => updateTheme('background.image_url', url)}
                              endpoint="/upload/profile-background"
                              description="Will tile or cover the background"
                              aspectRatio="any"
                            />
                            {theme.background?.image_url && (
                              <div>
                                <Label className="text-gray-300 text-sm">
                                  Image Opacity: {Math.round((theme.background?.image_opacity || 0.2) * 100)}%
                                </Label>
                                <input
                                  type="range"
                                  min="5"
                                  max="100"
                                  value={(theme.background?.image_opacity || 0.2) * 100}
                                  onChange={(e) => updateTheme('background.image_opacity', e.target.value / 100)}
                                  className="w-full mt-2"
                                />
                              </div>
                            )}
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                  
                  {/* Extras Tab */}
                  <TabsContent value="extras" className="mt-0 space-y-6">
                    <Card className="bg-gray-800 border-gray-700">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-white flex items-center gap-2">
                          <Music className="w-4 h-4" />
                          Profile Music
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <p className="text-xs text-gray-400">
                          Add a Spotify or SoundCloud link to play music on your profile. 
                          Visitors can control playback.
                        </p>
                        
                        <div>
                          <Label className="text-gray-300 text-sm">Music URL</Label>
                          <Input
                            value={theme.music_url || ''}
                            onChange={(e) => updateTheme('music_url', e.target.value)}
                            placeholder="https://open.spotify.com/track/..."
                            className="bg-gray-700 border-gray-600 text-white mt-2"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Supports: Spotify tracks, albums, playlists • SoundCloud tracks
                          </p>
                        </div>
                        
                        {theme.music_url && (
                          <div className="flex items-center gap-2 text-green-400 text-sm">
                            <Check className="w-4 h-4" />
                            Music will appear on your profile when enabled in Sections
                          </div>
                        )}
                        
                        {!theme.sections?.show_music_player && theme.music_url && (
                          <div className="flex items-center gap-2 text-yellow-400 text-sm">
                            <AlertTriangle className="w-4 h-4" />
                            Enable "Music Player" in Layout → Visible Sections to show this
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                </div>
              </Tabs>
            </div>
            
            {/* Right Panel - Preview */}
            <div className="flex-1 flex flex-col bg-gray-950">
              {/* Preview Controls */}
              <div className="h-12 bg-gray-800 border-b border-gray-700 flex items-center justify-center gap-2">
                <Button
                  variant={previewMode === 'desktop' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setPreviewMode('desktop')}
                  className="text-white"
                >
                  <Monitor className="w-4 h-4 mr-2" />
                  Desktop
                </Button>
                <Button
                  variant={previewMode === 'mobile' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setPreviewMode('mobile')}
                  className="text-white"
                >
                  <Smartphone className="w-4 h-4 mr-2" />
                  Mobile
                </Button>
              </div>
              
              {/* Preview Area */}
              <div className="flex-1 overflow-auto flex items-start justify-center p-6">
                <div 
                  className={`
                    rounded-lg overflow-hidden border border-gray-700 transition-all
                    ${previewMode === 'mobile' ? 'w-[375px]' : 'w-full max-w-4xl'}
                  `}
                  style={getPreviewStyles()}
                >
                  <ThemePreview 
                    theme={theme} 
                    profileData={profileData}
                    isMobile={previewMode === 'mobile'}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-gray-400">Failed to load theme data</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Theme Preview Component
const ThemePreview = ({ theme, profileData, isMobile }) => {
  if (!theme) return null;
  
  const colors = theme.colors || {};
  const sections = theme.sections || {};
  const banner = theme.banner || {};
  const bg = theme.background || {};
  
  // Build background style
  let backgroundStyle = { backgroundColor: colors.background || '#1a1a2e' };
  if (bg.type === 'gradient' && bg.gradient_start && bg.gradient_end) {
    backgroundStyle = {
      background: `linear-gradient(${bg.gradient_direction || 'to bottom'}, ${bg.gradient_start}, ${bg.gradient_end})`
    };
  } else if (bg.type === 'image' && bg.image_url) {
    backgroundStyle = {
      backgroundColor: colors.background || '#1a1a2e',
      backgroundImage: `url(${bg.image_url})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    };
  }
  
  return (
    <div 
      className="min-h-[600px]"
      style={{
        ...backgroundStyle,
        fontFamily: theme.font || 'Inter',
        color: colors.text || '#eaeaea'
      }}
    >
      {/* Banner Section */}
      {banner.url ? (
        <div 
          className="relative h-48 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${banner.url})`,
            backgroundPosition: banner.position || 'center'
          }}
        >
          <div 
            className="absolute inset-0"
            style={{ backgroundColor: `rgba(0,0,0,${banner.overlay_opacity || 0.3})` }}
          />
        </div>
      ) : (
        <div 
          className="h-32"
          style={{ backgroundColor: colors.card || '#16213e' }}
        />
      )}
      
      {/* Profile Info */}
      <div className={`px-6 ${isMobile ? 'px-4' : 'px-8'} -mt-12 relative z-10`}>
        {/* Avatar */}
        <div 
          className="w-24 h-24 rounded-full border-4 bg-gray-700 flex items-center justify-center text-2xl font-bold"
          style={{ 
            borderColor: colors.accent || '#f97316',
            color: colors.heading || '#ffffff'
          }}
        >
          {profileData?.display_name?.[0]?.toUpperCase() || 'C'}
        </div>
        
        {/* Name & Handle */}
        <div className="mt-4">
          <h1 
            className="text-2xl font-bold"
            style={{ color: colors.heading || '#ffffff' }}
          >
            {profileData?.display_name || 'Creator Name'}
          </h1>
          <p style={{ color: colors.link || '#60a5fa' }}>
            @{profileData?.handle || 'handle'}
          </p>
        </div>
        
        {/* Bio */}
        {sections.show_bio !== false && (
          <div 
            className="mt-4 p-4 rounded-lg"
            style={{ backgroundColor: colors.card || '#16213e' }}
          >
            <p style={{ color: colors.text || '#eaeaea' }}>
              {profileData?.bio_richtext || 'Your bio will appear here. Tell visitors about yourself!'}
            </p>
          </div>
        )}
        
        {/* Stats */}
        {sections.show_stats !== false && (
          <div 
            className="mt-4 p-4 rounded-lg flex justify-around text-center"
            style={{ backgroundColor: colors.card || '#16213e' }}
          >
            <div>
              <div className="text-xl font-bold" style={{ color: colors.heading }}>0</div>
              <div className="text-sm" style={{ color: colors.text }}>Followers</div>
            </div>
            <div>
              <div className="text-xl font-bold" style={{ color: colors.heading }}>0</div>
              <div className="text-sm" style={{ color: colors.text }}>Series</div>
            </div>
            <div>
              <div className="text-xl font-bold" style={{ color: colors.heading }}>0</div>
              <div className="text-sm" style={{ color: colors.text }}>Views</div>
            </div>
          </div>
        )}
        
        {/* Series Section */}
        {sections.show_series !== false && (
          <div className="mt-6">
            <h2 
              className="text-lg font-bold mb-3"
              style={{ color: colors.heading || '#ffffff' }}
            >
              Comics & Novels
            </h2>
            <div 
              className="p-4 rounded-lg text-center"
              style={{ 
                backgroundColor: colors.card || '#16213e',
                borderColor: colors.accent || '#f97316',
                borderWidth: '1px',
                borderStyle: 'dashed'
              }}
            >
              <p style={{ color: colors.text }}>Your content will appear here</p>
            </div>
          </div>
        )}
        
        {/* Gallery Section */}
        {sections.show_gallery !== false && (
          <div className="mt-6">
            <h2 
              className="text-lg font-bold mb-3"
              style={{ color: colors.heading || '#ffffff' }}
            >
              Art Gallery
            </h2>
            <div className="grid grid-cols-3 gap-2">
              {[1, 2, 3].map(i => (
                <div 
                  key={i}
                  className="aspect-square rounded-lg"
                  style={{ backgroundColor: colors.card || '#16213e' }}
                />
              ))}
            </div>
          </div>
        )}
        
        {/* Music Player */}
        {sections.show_music_player && theme.music_url && (
          <div className="mt-6">
            <h2 
              className="text-lg font-bold mb-3"
              style={{ color: colors.heading || '#ffffff' }}
            >
              🎵 Now Playing
            </h2>
            <div 
              className="p-4 rounded-lg"
              style={{ backgroundColor: colors.card || '#16213e' }}
            >
              <p className="text-sm" style={{ color: colors.text }}>
                Music player will embed here
              </p>
            </div>
          </div>
        )}
        
        {/* Social Links */}
        {sections.show_social_links !== false && (
          <div className="mt-6 mb-8">
            <h2 
              className="text-lg font-bold mb-3"
              style={{ color: colors.heading || '#ffffff' }}
            >
              Connect
            </h2>
            <div className="flex gap-3">
              {['Twitter', 'Instagram', 'Website'].map(link => (
                <button
                  key={link}
                  className="px-4 py-2 rounded-lg text-sm font-medium"
                  style={{ 
                    backgroundColor: colors.accent || '#f97316',
                    color: colors.heading || '#ffffff'
                  }}
                >
                  {link}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileThemeCustomizer;
